/**
 * @fileoverview Investments page — Investment Portfolio Composition.
 * @description Ported from the InsightBox Banking design system's own
 * `Investments/InvestmentPortfolioComposition.tsx`.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useCurrentSession } from '@/features/auth'
import {
  BranchFilterSelect,
  FIVE_YEAR_ID,
  FyFilterSelect,
  LastUpdatedLabel,
  branchLabel,
  formatCr,
  investmentMix,
  resolveBankingAccessScope,
  resolveFy,
  useBankingData,
} from '@/features/banking'
import BankingKpiStrip from '@/shared/components/molecules/BankingKpiStrip'
import BusinessSummaryPanel from '@/shared/components/molecules/BusinessSummaryPanel'
import ChartCard from '@/shared/components/molecules/ChartCard'
import PageHeader from '@/shared/components/molecules/PageHeader'
import RankedBarList from '@/shared/components/molecules/RankedBarList'
import { categoricalPalette } from '@/shared/constants/chart-theme.constants'

import type { FyFilter } from '@/features/banking'
import type { BankingKpiStripItem } from '@/shared/components/molecules/BankingKpiStrip'
import type { RankedBarListItem } from '@/shared/components/molecules/RankedBarList'

/**
 * @component
 */
function BankingInvestmentsPage() {
  const { t } = useTranslation()
  const dataQuery = useBankingData()
  const [fyFilter, setFyFilter] = useState<FyFilter>(FIVE_YEAR_ID)
  const bankingScope = resolveBankingAccessScope(useCurrentSession().data?.bankingScope)
  const [branchId, setBranchId] = useState(bankingScope.defaultBranchId)

  const header = (
    <PageHeader
      kicker={t('banking.investments.kicker')}
      title={t('banking.investments.title')}
      description={t('banking.investments.description')}
      filters={
        <>
          <FyFilterSelect value={fyFilter} onChange={setFyFilter} />
          <BranchFilterSelect value={branchId} onChange={setBranchId} scope={bankingScope} />
          {dataQuery.data && <LastUpdatedLabel value={dataQuery.data.meta.lastUpdated} />}
        </>
      }
    />
  )

  if (dataQuery.isPending) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <BankingKpiStrip
          loading
          items={[
            { label: '', value: '', highlight: true },
            { label: '', value: '' },
            { label: '', value: '' },
            { label: '', value: '' },
          ]}
        />
        <ChartCard title="" loading>
          <div />
        </ChartCard>
      </div>
    )
  }

  if (dataQuery.isError) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <p className="text-sm text-danger">{t('banking.loadError')}</p>
      </div>
    )
  }

  const data = dataQuery.data
  const fy = resolveFy(fyFilter)
  const mix = investmentMix(data, fy, branchId)
  const total = mix.reduce((sum, m) => sum + m.amountCr, 0)
  const colors = categoricalPalette(mix.length)
  const gsec = mix.find((m) => m.key === 'gsec')!
  const other = mix.find((m) => m.key === 'other-bank')!
  const mf = mix.find((m) => m.key === 'mf-shares')!

  const chartItems: RankedBarListItem[] = mix.map((m, i) => ({
    key: m.key,
    label: m.label,
    value: m.amountCr,
    displayValue: `${formatCr(m.amountCr)} (${m.sharePct.toFixed(2)}%)`,
    color: colors[i]!,
  }))

  const kpis: BankingKpiStripItem[] = [
    {
      label: t('banking.investments.kpi.totalInvestments'),
      value: formatCr(total),
      sublabel: t('banking.investments.kpi.portfolioValue'),
      highlight: true,
    },
    {
      label: t('banking.investments.kpi.gsecHoldings'),
      value: formatCr(gsec.amountCr),
      sublabel: t('banking.investments.kpi.ofInvestmentBook', {
        pct: gsec.sharePct.toFixed(2),
      }),
    },
    {
      label: t('banking.investments.kpi.otherBankDeposits'),
      value: formatCr(other.amountCr),
      sublabel: t('banking.investments.kpi.ofInvestmentBook', {
        pct: other.sharePct.toFixed(2),
      }),
    },
    {
      label: t('banking.investments.kpi.mfAndShares'),
      value: formatCr(mf.amountCr),
      sublabel: t('banking.investments.kpi.ofInvestmentBook', { pct: mf.sharePct.toFixed(2) }),
    },
  ]

  return (
    <div className="flex flex-col gap-4">
      {header}
      <BankingKpiStrip items={kpis} />

      <ChartCard
        title={t('banking.investments.chartTitle')}
        subtitle={t('banking.investments.chartSubtitle', { branch: branchLabel(branchId), fy })}
      >
        <div className="flex flex-col gap-5">
          <RankedBarList items={chartItems} labelWidth={150} scaleBy="total" />
          <div className="flex shrink-0 items-center justify-between border-t border-border pt-4">
            <p className="text-sm font-semibold text-text">
              {t('banking.investments.kpi.totalInvestments')}
            </p>
            <p className="text-sm font-bold text-primary">{formatCr(total)} (100.00%)</p>
          </div>
        </div>
      </ChartCard>

      <BusinessSummaryPanel
        title={t('banking.businessSummary')}
        lines={[
          t('banking.investments.summary.line1', { share: gsec.sharePct.toFixed(2) }),
          t('banking.investments.summary.line2', {
            share: (other.sharePct + mf.sharePct).toFixed(2),
          }),
        ]}
      />
    </div>
  )
}

export default BankingInvestmentsPage
